import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import { RootState } from '../../../stateStore'
import subCategoryListApi from '../../../middleware/api/ExpenseManagement/Sub-Category/subCategoryApi'
import {
  CategoryList,
  SubCategoryList,
  SubCategoryListSliceState,
} from '../../../types/ExpenseManagement/Sub-Category/subCategoryListTypes'

const getCategoryList = createAsyncThunk(
  '/ExpenseManagement/getCategoryList',
  async (_, thunkApi) => {
    try {
      return await subCategoryListApi.getCategoryList()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getSubCategoryList = createAsyncThunk(
  '/ExpenseManagement/getSubCategoryList',
  async (_, thunkApi) => {
    try {
      return await subCategoryListApi.getSubCategoryList()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const duplicateSubCategoryList = createAsyncThunk(
  '/ExpenseManagement/duplicateSubCategoryList',
  async (
    {
      categoryId,
      subCategoryName,
    }: {
      categoryId: number
      subCategoryName: string
    },
    thunkApi,
  ) => {
    try {
      return await subCategoryListApi.duplicateSubCategoryList(
        categoryId,
        subCategoryName,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const addSubCategoryList = createAsyncThunk(
  '/ExpenseManagement/addSubCategoryList',
  async (
    {
      categoryId,
      subCategoryName,
    }: {
      categoryId: number
      subCategoryName: string
    },
    thunkApi,
  ) => {
    try {
      return await subCategoryListApi.addSubCategoryList({
        categoryId,
        subCategoryName,
      })
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const editExpenseSubCategoryList = createAsyncThunk(
  '/ExpenseManagement/editSubCategory',
  async (categoryId: number, thunkApi) => {
    try {
      return await subCategoryListApi.editSubCategoryList(categoryId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const updateExpenseSubCategoryList = createAsyncThunk(
  '/ExpenseManagement/updateCategory',
  async (data: SubCategoryList, thunkApi) => {
    try {
      return await subCategoryListApi.updateSubCategoryList(data)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const deleteExpenseSubCategoryList = createAsyncThunk(
  '/ExpenseManagement/deleteCategory',
  async (categoryId: number, thunkApi) => {
    try {
      return await subCategoryListApi.deleteSubCategoryList(categoryId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const initialExpenseSubCategoryState: SubCategoryListSliceState = {
  isLoading: ApiLoadingState.idle,
  expenseCategories: [],
  subExpenseCategories: [],
  currentPage: 1,
  pageSize: 20,
  isLoadingAdd: ApiLoadingState.idle,
  isLoadingDelete: ApiLoadingState.idle,
}

const subCategoryListSlice = createSlice({
  name: 'sub category',
  initialState: initialExpenseSubCategoryState,
  reducers: {
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCategoryList.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.expenseCategories = action.payload
      })
      .addCase(addSubCategoryList.fulfilled, (state) => {
        state.isLoadingAdd = ApiLoadingState.succeeded
      })
      .addCase(addSubCategoryList.pending, (state) => {
        state.isLoadingAdd = ApiLoadingState.loading
      })
      .addCase(addSubCategoryList.rejected, (state) => {
        state.isLoadingAdd = ApiLoadingState.failed
      })
      .addCase(deleteExpenseSubCategoryList.fulfilled, (state) => {
        state.isLoadingDelete = ApiLoadingState.succeeded
      })
      .addCase(deleteExpenseSubCategoryList.pending, (state) => {
        state.isLoadingDelete = ApiLoadingState.loading
      })
      .addCase(deleteExpenseSubCategoryList.rejected, (state) => {
        state.isLoadingDelete = ApiLoadingState.failed
      })
      .addMatcher(
        isAnyOf(getSubCategoryList.fulfilled, addSubCategoryList.fulfilled),
        (state, action) => {
          state.isLoading = ApiLoadingState.succeeded
          state.subExpenseCategories = action.payload
        },
      )
      .addMatcher(
        isAnyOf(
          deleteExpenseSubCategoryList.fulfilled,
          duplicateSubCategoryList.fulfilled,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.succeeded
        },
      )

      .addMatcher(
        isAnyOf(
          getCategoryList.pending,
          getSubCategoryList.pending,
          addSubCategoryList.pending,
          editExpenseSubCategoryList.pending,
          updateExpenseSubCategoryList.pending,
          deleteExpenseSubCategoryList.pending,
          duplicateSubCategoryList.pending,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.loading
        },
      )
      .addMatcher(
        isAnyOf(
          getCategoryList.rejected,
          getSubCategoryList.rejected,
          addSubCategoryList.rejected,
          editExpenseSubCategoryList.rejected,
          updateExpenseSubCategoryList.rejected,
          deleteExpenseSubCategoryList.rejected,
          duplicateSubCategoryList.rejected,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.failed
        },
      )
  },
})

const isLoading = (state: RootState): LoadingState =>
  state.subCategoryList.isLoading
const categories = (state: RootState): CategoryList[] =>
  state.subCategoryList.expenseCategories
const subCategories = (state: RootState): SubCategoryList[] =>
  state.subCategoryList.subExpenseCategories
const addSubCategories = (state: RootState): SubCategoryList[] =>
  state.subCategoryList.subExpenseCategories
const duplicateSubCategories = (state: RootState): SubCategoryList[] =>
  state.subCategoryList.subExpenseCategories
const pageFromState = (state: RootState): number =>
  state.subCategoryList.currentPage
const pageSizeFromState = (state: RootState): number =>
  state.subCategoryList.pageSize
const getIsLoadingAdd = (state: RootState): LoadingState =>
  state.subCategoryList.isLoadingAdd
const getIsLoadingDelete = (state: RootState): LoadingState =>
  state.subCategoryList.isLoadingDelete

const subCategoryListThunk = {
  getCategoryList,
  getSubCategoryList,
  addSubCategoryList,
  editExpenseSubCategoryList,
  updateExpenseSubCategoryList,
  deleteExpenseSubCategoryList,
  duplicateSubCategoryList,
}

const subCategoryListSelectors = {
  isLoading,
  categories,
  subCategories,
  addSubCategories,
  pageFromState,
  pageSizeFromState,
  duplicateSubCategories,
  getIsLoadingAdd,
  getIsLoadingDelete,
}

export const subCategoryListService = {
  ...subCategoryListThunk,
  actions: subCategoryListSlice.actions,
  selectors: subCategoryListSelectors,
}

export default subCategoryListSlice.reducer
